import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import type { User, Organization, OrgRole, AuthResult } from "@/types";

interface ProfileLite {
  first_name?: string; last_name?: string; email?: string;
  avatar_url?: string | null; email_verified?: boolean;
}
type AuthState = User | null | false; // null = checking, false = signed out

interface AuthContextValue {
  user: AuthState;
  profile: ProfileLite | null;
  organization: Organization | null;
  role: OrgRole | null;
  isPlatformAdmin: boolean;
  emailVerified: boolean;
  mfaEnabled: boolean;
  mfaRequired: boolean;
  mfaEnrollmentRequired: boolean;
  login: (email: string, password: string) => Promise<AuthResult>;
  finishLogin: (token: string) => Promise<void>;
  register: (payload: {
    email: string; password: string; first_name: string; last_name: string;
    organization_name: string; industry: string;
  }) => Promise<{ redirect_to: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthState>(null);
  const [profile, setProfile] = useState<ProfileLite | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [role, setRole] = useState<OrgRole | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);

  const hydrate = useCallback(async () => {
    const data = await api.me();
    setUser(data.user);
    setProfile((data as unknown as { profile: ProfileLite }).profile ?? null);
    setOrganization(data.organization);
    setRole(data.role);
    setEmailVerified(data.email_verified ?? false);
    setMfaEnabled(data.mfa_enabled ?? false);
    setMfaRequired(data.mfa_required ?? false);
    setMfaEnrollmentRequired(data.mfa_enrollment_required ?? false);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("resfil_token")) { setUser(false); return; }
    hydrate().catch(() => { localStorage.removeItem("resfil_token"); setUser(false); });
  }, [hydrate]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password);
    if (res.token) {
      localStorage.setItem("resfil_token", res.token);
      await hydrate();
    }
    return res;
  }, [hydrate]);

  const finishLogin = useCallback(async (token: string) => {
    localStorage.setItem("resfil_token", token);
    await hydrate();
  }, [hydrate]);

  const register = useCallback(async (payload: Parameters<typeof api.register>[0]) => {
    const res = await api.register(payload);
    localStorage.setItem("resfil_token", res.token as string);
    await hydrate();
    return { redirect_to: res.redirect_to };
  }, [hydrate]);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch { /* ignore */ }
    localStorage.removeItem("resfil_token");
    setUser(false); setProfile(null); setOrganization(null); setRole(null);
    setEmailVerified(false); setMfaEnabled(false); setMfaRequired(false); setMfaEnrollmentRequired(false);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, profile, organization, role,
      isPlatformAdmin: !!(user && typeof user === "object" && (user as { platform_role?: string }).platform_role === "platform_admin"),
      emailVerified, mfaEnabled, mfaRequired, mfaEnrollmentRequired,
      login, finishLogin, register, logout, refresh: hydrate,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
